import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Bot, X, ChevronRight, AlertTriangle, Zap, MessageCircle,
  Send, Loader2, FileText, Upload, Eye, Shield, Check,
  ArrowRight, Clock, BarChart3, CircleDot, Sparkles,
  ChevronDown, ChevronUp, Users, ExternalLink, Bell,
  DollarSign, AlertCircle, Activity, Brain
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITY_STYLES = {
  high: { dot: "bg-red-500", bg: "bg-red-50 border-red-200", text: "text-red-700", label: "Urgent" },
  medium: { dot: "bg-amber-500", bg: "bg-amber-50 border-amber-200", text: "text-amber-700", label: "Action Needed" },
  low: { dot: "bg-emerald-500", bg: "bg-emerald-50 border-emerald-200", text: "text-emerald-700", label: "Info" },
};

const SEVERITY_MAP = {
  critical: { dot: "bg-red-600", bg: "bg-red-50 border-red-300", text: "text-red-700", badge: "bg-red-100 text-red-800" },
  high: { dot: "bg-orange-500", bg: "bg-orange-50 border-orange-200", text: "text-orange-700", badge: "bg-orange-100 text-orange-800" },
  medium: { dot: "bg-amber-500", bg: "bg-amber-50 border-amber-200", text: "text-amber-700", badge: "bg-amber-100 text-amber-800" },
  low: { dot: "bg-blue-400", bg: "bg-blue-50 border-blue-200", text: "text-blue-600", badge: "bg-blue-100 text-blue-800" },
};

const TASK_ICONS = {
  stage_action: ArrowRight, approval: Shield, document: Upload,
  action_required: AlertTriangle, user_approval: Users,
  milestone: Check, info: CircleDot, notification: MessageCircle, exception: AlertCircle,
};

const SIGNAL_ICONS = {
  stalled_deal: Clock, unfunded_deal: DollarSign, inactive_investor: Users,
  missing_documents: FileText, funding_delay: AlertCircle, exception_trigger: AlertTriangle,
};

// ── Task Item (V1 tasks) ──
function TaskItem({ task, onAction }) {
  const ps = PRIORITY_STYLES[task.priority] || PRIORITY_STYLES.low;
  const Icon = TASK_ICONS[task.type] || CircleDot;
  const isClickable = !!(task.lead_id || task.user_id || task.action);
  return (
    <div className={`p-3 rounded-xl border ${ps.bg} transition-all ${isClickable ? "cursor-pointer hover:shadow-md hover:scale-[1.01]" : "hover:shadow-sm"}`}
      data-testid={`copilot-task-${task.id}`}
      onClick={isClickable ? () => onAction(task) : undefined}
      role={isClickable ? "button" : undefined} tabIndex={isClickable ? 0 : undefined}>
      <div className="flex items-start gap-2.5">
        <div className={`mt-0.5 w-2 h-2 rounded-full shrink-0 ${ps.dot}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <Icon className={`h-3.5 w-3.5 shrink-0 ${ps.text}`} />
            <span className="text-xs font-semibold text-[#0A0A0A] truncate">{task.title}</span>
          </div>
          <p className="text-[11px] text-[#666] mt-0.5 line-clamp-2">{task.description}</p>
          {task.action_label && (
            <button onClick={(e) => { e.stopPropagation(); onAction(task); }} data-testid={`copilot-action-${task.id}`}
              className={`mt-2 flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold ${ps.text} border ${ps.bg} hover:shadow-sm transition-all`}>
              {task.action_label} <ChevronRight className="h-3 w-3" />
            </button>
          )}
        </div>
        {task.hours_waiting > 0 && (
          <span className="flex items-center gap-0.5 text-[9px] text-[#999] shrink-0 mt-0.5">
            <Clock className="h-2.5 w-2.5" /> {task.hours_waiting >= 48 ? `${Math.round(task.hours_waiting / 24)}d` : `${task.hours_waiting}h`}
          </span>
        )}
      </div>
    </div>
  );
}

// ── Signal Item (V2 signals with action confirmation) ──
function SignalItem({ signal, onNavigate, onConfirmAction }) {
  const sev = SEVERITY_MAP[signal.severity] || SEVERITY_MAP.medium;
  const Icon = SIGNAL_ICONS[signal.signal_type] || Activity;
  const rec = signal.recommended_action || {};

  return (
    <div className={`p-3 rounded-xl border ${sev.bg} transition-all hover:shadow-sm`} data-testid={`signal-${signal.signal_id}`}>
      <div className="flex items-start gap-2.5">
        <div className={`mt-0.5 w-2 h-2 rounded-full shrink-0 ${sev.dot}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            <Icon className={`h-3.5 w-3.5 shrink-0 ${sev.text}`} />
            <span className="text-xs font-semibold text-[#0A0A0A] truncate">{signal.title}</span>
            <span className={`ml-auto px-1.5 py-0.5 rounded text-[8px] font-bold uppercase ${sev.badge}`}>
              {signal.severity}
            </span>
          </div>
          <p className="text-[11px] text-[#666] mt-0.5 line-clamp-2">{signal.description}</p>
          <div className="flex items-center gap-2 mt-2">
            {signal.entity_type && signal.entity_id && (
              <button onClick={() => onNavigate(signal.entity_type, signal.entity_id)}
                data-testid={`signal-navigate-${signal.signal_id}`}
                className="flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-semibold text-emerald-700 border border-emerald-200 bg-emerald-50 hover:shadow-sm transition-all">
                <ExternalLink className="h-2.5 w-2.5" /> View {signal.entity_type}
              </button>
            )}
            {rec.label && (
              <button onClick={() => onConfirmAction(signal)}
                data-testid={`signal-action-${signal.signal_id}`}
                className={`flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-bold ${sev.text} border ${sev.bg} hover:shadow-sm transition-all`}>
                <Zap className="h-2.5 w-2.5" /> {rec.label}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Action Confirmation Dialog ──
function ActionConfirmDialog({ signal, onConfirm, onCancel, loading }) {
  if (!signal) return null;
  const rec = signal.recommended_action || {};
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="absolute inset-0 z-10 bg-white/95 backdrop-blur-sm flex items-center justify-center p-6">
      <div className="w-full max-w-sm space-y-4" data-testid="action-confirm-dialog">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl bg-amber-100 flex items-center justify-center">
            <Shield className="h-4 w-4 text-amber-700" />
          </div>
          <div>
            <h4 className="text-sm font-bold text-[#0A0A0A]">Confirm Action</h4>
            <p className="text-[10px] text-[#888]">This action requires your approval</p>
          </div>
        </div>
        <div className="p-3 rounded-xl border border-[#e8e0d0] bg-[#faf7f2]">
          <p className="text-xs font-semibold text-[#333]">{rec.label}</p>
          <p className="text-[11px] text-[#666] mt-1">{rec.description}</p>
          <p className="text-[10px] text-[#999] mt-2">Signal: {signal.title}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={onCancel} disabled={loading} data-testid="action-cancel"
            className="flex-1 px-4 py-2 rounded-xl border border-[#e8e0d0] text-xs font-semibold text-[#666] hover:bg-[#faf7f2] transition-colors">
            Cancel
          </button>
          <button onClick={() => onConfirm(signal)} disabled={loading} data-testid="action-confirm"
            className="flex-1 px-4 py-2 rounded-xl bg-emerald-700 text-white text-xs font-bold hover:bg-emerald-600 transition-colors flex items-center justify-center gap-1.5">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
            Confirm & Execute
          </button>
        </div>
      </div>
    </motion.div>
  );
}

// ── Blocker Item ──
function BlockerItem({ blocker, onNavigate }) {
  const isSignal = !!blocker.signal_id;
  const sev = isSignal ? SEVERITY_MAP[blocker.severity] || SEVERITY_MAP.medium : { bg: "bg-red-50", border: "border-red-300", icon: "text-red-500" };

  return (
    <div className={`p-3 rounded-xl border ${isSignal ? sev.bg : `${sev.border} ${sev.bg}`}`} data-testid={`copilot-blocker-${blocker.signal_id || blocker.lead_id}`}>
      <div className="flex items-start gap-2.5">
        <AlertTriangle className={`h-4 w-4 shrink-0 mt-0.5 ${isSignal ? sev.text : sev.icon}`} />
        <div className="flex-1 min-w-0">
          <span className="text-xs font-semibold text-[#0A0A0A]">{blocker.title}</span>
          <p className="text-[11px] text-[#666] mt-0.5">{blocker.description}</p>
          {(blocker.entity_id || blocker.lead_id) && (
            <button onClick={() => onNavigate(blocker.entity_type || "deal", blocker.entity_id || blocker.lead_id)}
              className="mt-1.5 flex items-center gap-1 text-[10px] font-semibold text-emerald-700 hover:underline">
              <ExternalLink className="h-2.5 w-2.5" /> View {blocker.entity_type || "Deal"}
            </button>
          )}
        </div>
        {isSignal && (
          <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold uppercase ${sev.badge}`}>
            {blocker.severity}
          </span>
        )}
      </div>
    </div>
  );
}

// ── Next Action Card ──
function NextActionCard({ action, signal, onAction, onNavigate }) {
  if (!action && !signal) return null;
  const hasSignal = !!signal;
  return (
    <div className="p-4 rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200" data-testid="copilot-next-action">
      <div className="flex items-center gap-2 mb-2">
        <Zap className="h-4 w-4 text-emerald-600" />
        <span className="text-xs font-bold text-emerald-800 uppercase tracking-wide">Next Best Action</span>
        {hasSignal && <span className="px-1.5 py-0.5 rounded text-[8px] font-bold bg-emerald-100 text-emerald-700 uppercase">AI-Driven</span>}
      </div>
      <p className="text-sm font-semibold text-[#0A0A0A]">{signal?.title || action?.title}</p>
      <p className="text-xs text-[#666] mt-1">{signal?.description || action?.description}</p>
      <p className="text-[10px] text-emerald-600 mt-1 italic">{signal?.recommended_action?.description || action?.reason}</p>
      <div className="flex items-center gap-2 mt-3">
        {(signal?.entity_id || action?.lead_id) && (
          <button onClick={() => onNavigate(signal?.entity_type || "deal", signal?.entity_id || action?.lead_id)}
            data-testid="copilot-next-action-navigate"
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-emerald-300 bg-emerald-50 text-emerald-700 text-xs font-bold hover:shadow-sm transition-colors">
            <ExternalLink className="h-3 w-3" /> View
          </button>
        )}
        {(signal?.recommended_action?.label || action?.action_label) && (
          <button onClick={() => onAction(signal || action)} data-testid="copilot-next-action-btn"
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-700 text-white text-xs font-bold hover:bg-emerald-600 transition-colors shadow-sm">
            {signal?.recommended_action?.label || action?.action_label} <ArrowRight className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

// ── Quick Summary ──
function QuickSummary({ summary, role, aiSummary, signalCounts }) {
  if (!summary) return null;

  const renderRoleSummary = () => {
    if (role === "borrower") {
      return (
        <div className="space-y-1.5">
          {(summary.deals || []).map(d => (
            <div key={d.lead_id} className="flex items-center justify-between text-xs">
              <span className="font-medium text-[#333]">{d.deal_number}</span>
              <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-bold border border-emerald-200">
                {d.stage_label}
              </span>
            </div>
          ))}
        </div>
      );
    }
    if (role === "investor") {
      return (
        <div className="grid grid-cols-2 gap-2">
          <div className="text-center p-2 rounded-lg bg-white border border-[#e8e0d0]">
            <div className="text-lg font-bold text-amber-600">{summary.pending_commitments || 0}</div>
            <div className="text-[9px] text-[#888] uppercase">Pending</div>
          </div>
          <div className="text-center p-2 rounded-lg bg-white border border-[#e8e0d0]">
            <div className="text-lg font-bold text-blue-600">{summary.confirmed_commitments || 0}</div>
            <div className="text-[9px] text-[#888] uppercase">Confirmed</div>
          </div>
          <div className="text-center p-2 rounded-lg bg-white border border-[#e8e0d0]">
            <div className="text-lg font-bold text-emerald-700">{summary.funded_commitments || 0}</div>
            <div className="text-[9px] text-[#888] uppercase">Funded</div>
          </div>
          <div className="text-center p-2 rounded-lg bg-white border border-[#e8e0d0]">
            <div className="text-lg font-bold text-emerald-700">
              ${((summary.total_committed || 0) / 1e3).toFixed(0)}K
            </div>
            <div className="text-[9px] text-[#888] uppercase">Committed</div>
          </div>
        </div>
      );
    }
    if (role === "lender") {
      return (
        <div className="grid grid-cols-2 gap-2">
          <div className="text-center p-2 rounded-lg bg-white border border-[#e8e0d0]">
            <div className="text-lg font-bold text-[#0A0A0A]">{summary.active_interests || 0}</div>
            <div className="text-[9px] text-[#888] uppercase">Active Interests</div>
          </div>
          <div className="text-center p-2 rounded-lg bg-white border border-[#e8e0d0]">
            <div className="text-lg font-bold text-blue-600">{summary.term_sheets_submitted || 0}</div>
            <div className="text-[9px] text-[#888] uppercase">Terms Submitted</div>
          </div>
        </div>
      );
    }
    // Admin
    return (
      <div className="grid grid-cols-2 gap-2">
        <div className="text-center p-2 rounded-lg bg-white border border-[#e8e0d0]">
          <div className="text-lg font-bold text-[#0A0A0A]">{summary.active_deals || 0}</div>
          <div className="text-[9px] text-[#888] uppercase">Active Deals</div>
        </div>
        <div className="text-center p-2 rounded-lg bg-white border border-[#e8e0d0]">
          <div className="text-lg font-bold text-[#0A0A0A]">{summary.pending_tasks || 0}</div>
          <div className="text-[9px] text-[#888] uppercase">Tasks</div>
        </div>
        <div className="text-center p-2 rounded-lg bg-white border border-[#e8e0d0]">
          <div className="text-lg font-bold text-red-500">{summary.stalled_count || 0}</div>
          <div className="text-[9px] text-[#888] uppercase">Stalled</div>
        </div>
        <div className="text-center p-2 rounded-lg bg-white border border-[#e8e0d0]">
          <div className="text-lg font-bold text-emerald-700">
            ${((summary.total_pipeline_volume || 0) / 1e6).toFixed(1)}M
          </div>
          <div className="text-[9px] text-[#888] uppercase">Pipeline Vol</div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-3" data-testid="copilot-summary">
      {/* AI Summary */}
      {aiSummary && (
        <div className="p-3 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Sparkles className="h-3 w-3 text-blue-600" />
            <span className="text-[10px] font-bold text-blue-700 uppercase tracking-wide">AI Analysis</span>
          </div>
          <p className="text-[11px] text-[#444] leading-relaxed">{aiSummary}</p>
        </div>
      )}

      {/* Signal severity breakdown */}
      {signalCounts && signalCounts.total > 0 && (
        <div className="p-3 rounded-xl bg-[#faf7f2] border border-[#e8e0d0]">
          <div className="flex items-center gap-1.5 mb-2">
            <Activity className="h-3 w-3 text-[#888]" />
            <span className="text-[10px] font-bold text-[#888] uppercase tracking-wide">Signal Health</span>
          </div>
          <div className="flex gap-2">
            {signalCounts.critical > 0 && (
              <span className="px-2 py-1 rounded-lg text-[10px] font-bold bg-red-100 text-red-700 border border-red-200">
                {signalCounts.critical} Critical
              </span>
            )}
            {signalCounts.high > 0 && (
              <span className="px-2 py-1 rounded-lg text-[10px] font-bold bg-orange-100 text-orange-700 border border-orange-200">
                {signalCounts.high} High
              </span>
            )}
            {signalCounts.medium > 0 && (
              <span className="px-2 py-1 rounded-lg text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-200">
                {signalCounts.medium} Medium
              </span>
            )}
          </div>
        </div>
      )}

      {/* Role-specific summary */}
      <div className="p-3 rounded-xl bg-[#faf7f2] border border-[#e8e0d0]">
        <div className="flex items-center gap-2 mb-2">
          <BarChart3 className="h-3.5 w-3.5 text-[#888]" />
          <span className="text-[10px] font-bold text-[#888] uppercase tracking-wide">
            {role === "borrower" ? "Your Deals" : role === "lender" ? "Lending Pipeline" : role === "investor" ? "Portfolio" : "Pipeline Snapshot"}
          </span>
        </div>
        {renderRoleSummary()}
      </div>
    </div>
  );
}

// ── Chat Section (V2 - with signal context) ──
function ChatSection({ leadId }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);
  const token = localStorage.getItem("ic_token");

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: text }]);
    setLoading(true);
    try {
      // Use V2 chat endpoint with signal context
      const res = await axios.post(`${API}/copilot/v2/chat`,
        { message: text, lead_id: leadId, include_signals: true },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 20000 }
      );
      const status = res.data.status;
      setMessages(prev => [...prev, {
        role: "assistant",
        content: res.data.response,
        meta: status === "fallback" ? "Offline mode" : "AI",
      }]);
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "Sorry, I couldn't process that. Try again.", meta: "Error" }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col" data-testid="copilot-chat-section">
      <div className="flex-1 space-y-2 max-h-48 overflow-y-auto mb-2">
        {messages.length === 0 && (
          <div className="text-center py-4 space-y-2">
            <p className="text-[11px] text-[#999]">Ask me about signals, deal status, next steps, or blockers.</p>
            <div className="flex flex-wrap gap-1 justify-center">
              {["What needs attention?", "Pipeline status", "Any blockers?"].map(q => (
                <button key={q} onClick={() => { setInput(q); }}
                  className="px-2 py-1 rounded-lg text-[10px] font-medium text-emerald-700 border border-emerald-200 bg-emerald-50 hover:shadow-sm transition-all">
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-xl px-3 py-2 text-xs leading-relaxed ${
              m.role === "user"
                ? "bg-emerald-700 text-white rounded-br-md"
                : "bg-[#faf7f2] border border-[#e8e0d0] text-[#333] rounded-bl-md"
            }`}>
              {m.content}
              {m.meta && <span className="block text-[9px] opacity-50 mt-0.5">{m.meta}</span>}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-[#faf7f2] border border-[#e8e0d0] rounded-xl px-3 py-2 rounded-bl-md">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-emerald-600" />
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>
      <div className="flex gap-2">
        <input value={input} onChange={e => setInput(e.target.value)} data-testid="copilot-chat-input"
          onKeyDown={e => e.key === "Enter" && sendMessage()}
          placeholder="Ask the copilot..."
          className="flex-1 text-xs px-3 py-2 rounded-xl border border-[#e8e0d0] bg-[#faf7f2] text-[#0A0A0A] placeholder:text-[#bbb] focus:outline-none focus:border-emerald-300" />
        <button onClick={sendMessage} disabled={loading || !input.trim()} data-testid="copilot-chat-send"
          className="p-2 rounded-xl bg-emerald-700 text-white disabled:opacity-40 hover:bg-emerald-600 transition-colors">
          <Send className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

// ── AI Memory Section ──
function MemorySection({ leadId }) {
  const [memories, setMemories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState("");
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem("ic_token");

  const entityType = leadId ? "deal" : "general";
  const entityId = leadId || "platform";

  const fetchMemories = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/ai/memory`, {
        params: { entity_type: entityType, entity_id: entityId },
        headers: { Authorization: `Bearer ${token}` },
      });
      setMemories(res.data.memories || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [token, entityType, entityId]);

  useEffect(() => { fetchMemories(); }, [fetchMemories]);

  const addNote = async () => {
    if (!noteText.trim() || saving) return;
    setSaving(true);
    try {
      await axios.post(`${API}/ai/memory`, {
        entity_type: entityType,
        entity_id: entityId,
        memory_type: "user_note",
        content: noteText.trim(),
      }, { headers: { Authorization: `Bearer ${token}` } });
      setNoteText("");
      fetchMemories();
    } catch { /* silent */ }
    finally { setSaving(false); }
  };

  const TYPE_STYLES = {
    chat_insight: { label: "AI Insight", color: "text-blue-700 bg-blue-50 border-blue-200" },
    user_note: { label: "Your Note", color: "text-amber-700 bg-amber-50 border-amber-200" },
    system: { label: "System", color: "text-gray-600 bg-gray-50 border-gray-200" },
  };

  return (
    <div className="space-y-3" data-testid="copilot-memory-section">
      {/* Add Note */}
      <div className="p-3 rounded-xl border border-[#e8e0d0] bg-[#faf7f2]">
        <div className="flex items-center gap-1.5 mb-2">
          <Brain className="h-3 w-3 text-purple-600" />
          <span className="text-[10px] font-bold text-[#888] uppercase tracking-wide">Add Memory Note</span>
        </div>
        <div className="flex gap-2">
          <input value={noteText} onChange={e => setNoteText(e.target.value)}
            onKeyDown={e => e.key === "Enter" && addNote()}
            placeholder="Add context for the AI to remember..."
            data-testid="memory-note-input"
            className="flex-1 text-xs px-3 py-2 rounded-xl border border-[#e8e0d0] bg-white text-[#0A0A0A] placeholder:text-[#bbb] focus:outline-none focus:border-purple-300" />
          <button onClick={addNote} disabled={saving || !noteText.trim()} data-testid="memory-note-save"
            className="p-2 rounded-xl bg-purple-700 text-white disabled:opacity-40 hover:bg-purple-600 transition-colors">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>

      {/* Memory List */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-purple-600" />
        </div>
      ) : memories.length === 0 ? (
        <div className="text-center py-8 text-sm text-[#bbb]">
          <Brain className="h-8 w-8 mx-auto mb-2 opacity-30" />
          <p>No memories yet.</p>
          <p className="text-[10px] mt-1">Chat with the AI or add notes — they'll appear here for future context.</p>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold text-[#888] uppercase tracking-wide">{memories.length} Memories</span>
            <span className="text-[9px] text-[#bbb]">{entityType === "deal" ? "Deal-specific" : "Global"}</span>
          </div>
          {memories.map((m, i) => {
            const style = TYPE_STYLES[m.memory_type] || TYPE_STYLES.system;
            const timeAgo = m.created_at ? new Date(m.created_at).toLocaleDateString() : "";
            return (
              <div key={m.memory_id || i} className="p-3 rounded-xl border border-[#e8e0d0] bg-white" data-testid={`memory-item-${i}`}>
                <div className="flex items-center gap-1.5 mb-1">
                  <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold border ${style.color}`}>
                    {style.label}
                  </span>
                  {timeAgo && <span className="text-[9px] text-[#bbb] ml-auto">{timeAgo}</span>}
                </div>
                <p className="text-[11px] text-[#444] leading-relaxed">{m.content}</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const ROLE_LABELS = {
  admin: "AI-powered mission control",
  borrower: "Your deal assistant",
  lender: "Lending pipeline assistant",
  investor: "Investment portfolio assistant",
};

export default function CopilotPanel({ focusedLeadId }) {
  const [open, setOpen] = useState(false);
  const [context, setContext] = useState(null);
  const [signals, setSignals] = useState([]);
  const [signalCounts, setSignalCounts] = useState(null);
  const [v2Summary, setV2Summary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState("tasks");
  const [greeting, setGreeting] = useState(null);
  const [confirmSignal, setConfirmSignal] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const navigate = useNavigate();
  const token = localStorage.getItem("ic_token");
  const greetingShownRef = useRef(false);

  // Fetch V1 context
  const fetchContext = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const url = focusedLeadId ? `${API}/copilot/context?lead_id=${focusedLeadId}` : `${API}/copilot/context`;
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setContext(res.data);
      return res.data;
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [token, focusedLeadId]);

  // Fetch V2 signals (non-blocking)
  const fetchSignals = useCallback(async () => {
    if (!token) return;
    try {
      const [sigRes, sumRes] = await Promise.allSettled([
        axios.get(`${API}/copilot/v2/signals`, { headers: { Authorization: `Bearer ${token}` }, timeout: 10000 }),
        axios.get(`${API}/copilot/v2/summary`, { headers: { Authorization: `Bearer ${token}` }, timeout: 20000 }),
      ]);
      if (sigRes.status === "fulfilled") {
        setSignals(sigRes.value.data.signals || []);
        setSignalCounts({ ...sigRes.value.data.severity_counts, total: sigRes.value.data.total });
      }
      if (sumRes.status === "fulfilled") {
        setV2Summary(sumRes.value.data);
      }
    } catch { /* silent */ }
  }, [token]);

  // Login greeting
  useEffect(() => {
    if (!token || greetingShownRef.current) return;
    const sessionKey = `copilot_greeted_${token.slice(-8)}`;
    if (sessionStorage.getItem(sessionKey)) return;

    const showGreeting = async () => {
      greetingShownRef.current = true;
      sessionStorage.setItem(sessionKey, "1");
      const ctx = await fetchContext();
      if (!ctx) return;

      const highTasks = (ctx.tasks || []).filter(t => t.priority === "high");
      const medTasks = (ctx.tasks || []).filter(t => t.priority === "medium");
      const topTasks = [...highTasks, ...medTasks].slice(0, 3);
      const topBlocker = (ctx.blockers || [])[0] || null;

      let userName = ctx.user_name || "there";
      if (userName.includes(" ")) userName = userName.split(" ")[0];

      setGreeting({
        userName, highCount: highTasks.length, totalCount: (ctx.tasks || []).length,
        topTasks, topBlocker, nextAction: ctx.next_action,
      });
      setOpen(true);
      // Fetch signals after context loads (non-blocking)
      fetchSignals();
    };

    const timer = setTimeout(showGreeting, 1500);
    return () => clearTimeout(timer);
  }, [token, fetchContext, fetchSignals]);

  // Fetch data when panel opens
  useEffect(() => {
    if (open && !context) fetchContext();
    if (open && signals.length === 0) fetchSignals();
  }, [open, context, signals.length, fetchContext, fetchSignals]);

  const handleAction = (task) => {
    const role = context?.role;
    // Navigation-based actions
    if (task.action === "view_marketplace" || task.action === "submit_term_sheet") {
      navigate("/lender"); setOpen(false); return;
    }
    if (task.action === "view_opportunities" || task.action === "view_commitment" || task.action === "view_distributions") {
      navigate("/investor"); setOpen(false); return;
    }
    // Navigate to entity 360 page for deal-related tasks
    if (task.lead_id) {
      const tab = task.action === "upload_documents" ? "documents" :
                  task.action === "view_deal_deck" ? "dealdeck" : "";
      navigate(`/entity/deal/${task.lead_id}${tab ? `?tab=${tab}` : ""}`);
      setOpen(false); return;
    }
    if (task.entity_type && task.entity_id) {
      navigate(`/entity/${task.entity_type}/${task.entity_id}`);
      setOpen(false); return;
    }
    if (task.user_id) {
      const entityType = task.entity_type || (role === "admin" ? "borrower" : role);
      navigate(`/entity/${entityType}/${task.user_id}`); setOpen(false); return;
    }
    // Fallback — navigate to dashboard
    if (role === "borrower") { navigate("/dashboard"); setOpen(false); return; }
    if (role === "investor") { navigate("/investor"); setOpen(false); return; }
    if (role === "lender") { navigate("/lender"); setOpen(false); return; }
    navigate("/admin"); setOpen(false);
  };

  const handleEntityNavigate = (entityType, entityId) => {
    navigate(`/entity/${entityType}/${entityId}`);
    setOpen(false);
  };

  const handleConfirmAction = async (signal) => {
    setConfirmLoading(true);
    try {
      const rec = signal.recommended_action || {};
      await axios.post(`${API}/copilot/v2/actions/confirm`, {
        signal_id: signal.signal_id,
        action_type: rec.action_type,
        entity_type: signal.entity_type,
        entity_id: signal.entity_id,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setConfirmSignal(null);
      // Refresh signals after action
      fetchSignals();
    } catch (err) {
      console.error("Action confirm error:", err);
    } finally {
      setConfirmLoading(false);
    }
  };

  // Merge V1 tasks + V2 signals for "Needs Attention"
  const mergedAttention = [
    ...signals.filter(s => s.severity === "critical" || s.severity === "high")
      .map(s => ({ ...s, _isSignal: true })),
    ...(context?.tasks || []).filter(t => t.priority === "high")
      .map(t => ({ ...t, _isSignal: false })),
    ...signals.filter(s => s.severity === "medium")
      .map(s => ({ ...s, _isSignal: true })),
    ...(context?.tasks || []).filter(t => t.priority !== "high")
      .map(t => ({ ...t, _isSignal: false })),
  ];

  // V2 blockers from signals
  const v2Blockers = [
    ...signals.filter(s => ["stalled_deal", "exception_trigger", "funding_delay"].includes(s.signal_type)),
    ...(context?.blockers || []),
  ];

  // V2 next best action from summary
  const nba = v2Summary?.next_best_action;

  const criticalCount = signalCounts?.critical || 0;
  const highCount = signalCounts?.high || 0;
  const totalBadge = criticalCount + highCount + ((context?.tasks || []).filter(t => t.priority === "high").length);

  const sections = [
    { key: "tasks", label: "Needs Attention", icon: AlertTriangle, badge: mergedAttention.length },
    { key: "next", label: "Next Action", icon: Zap },
    { key: "blockers", label: "Blockers", icon: Shield, badge: v2Blockers.length },
    { key: "chat", label: "AI Chat", icon: MessageCircle },
    { key: "memory", label: "Memory", icon: Brain },
    { key: "summary", label: "Summary", icon: BarChart3 },
  ];

  return (
    <>
      {/* Toggle Button */}
      <button onClick={() => setOpen(!open)} data-testid="copilot-toggle"
        className={`fixed right-4 bottom-16 z-[60] flex items-center gap-2 px-4 py-3 rounded-2xl shadow-lg border transition-all ${
          open ? "bg-[#0A0A0A] border-[#333] text-white" : "bg-emerald-700 border-emerald-600 text-white hover:bg-emerald-600"
        }`}>
        {open ? <X className="h-5 w-5" /> : <Bot className="h-5 w-5" />}
        {!open && <span className="text-sm font-bold">Ops Copilot</span>}
        {!open && totalBadge > 0 && (
          <span className="flex items-center justify-center w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold">
            {totalBadge > 9 ? "9+" : totalBadge}
          </span>
        )}
      </button>

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <motion.div initial={{ x: 400, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 400, opacity: 0 }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 bottom-0 z-[45] w-[380px] bg-white border-l border-[#e8e0d0] shadow-2xl flex flex-col"
            data-testid="copilot-panel">

            {/* Confirm Dialog Overlay */}
            <AnimatePresence>
              {confirmSignal && (
                <ActionConfirmDialog signal={confirmSignal}
                  onConfirm={handleConfirmAction} onCancel={() => setConfirmSignal(null)} loading={confirmLoading} />
              )}
            </AnimatePresence>

            {/* Header */}
            <div className="px-5 py-4 border-b border-[#e8e0d0] bg-[#faf7f2] shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-xl bg-emerald-700 flex items-center justify-center">
                    <Bot className="h-4.5 w-4.5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-[#0A0A0A]">Ops Copilot <span className="text-[9px] font-medium text-emerald-600 ml-1">V2</span></h3>
                    <p className="text-[10px] text-[#888]">
                      {ROLE_LABELS[context?.role] || "AI workflow assistant"}
                    </p>
                  </div>
                </div>
                <button onClick={() => setOpen(false)} className="p-1.5 rounded-lg hover:bg-[#e8e0d0] text-[#888]" data-testid="copilot-close">
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Section Tabs */}
              <div className="flex gap-1 mt-3 overflow-x-auto pb-0.5">
                {sections.map(s => (
                  <button key={s.key} onClick={() => setActiveSection(s.key)} data-testid={`copilot-tab-${s.key}`}
                    className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold whitespace-nowrap transition-colors ${
                      activeSection === s.key
                        ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                        : "text-[#888] hover:bg-[#e8e0d0] border border-transparent"
                    }`}>
                    <s.icon className="h-3 w-3" />
                    {s.label}
                    {s.badge > 0 && (
                      <span className={`ml-0.5 w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold ${
                        activeSection === s.key ? "bg-emerald-700 text-white" : "bg-[#e8e0d0] text-[#666]"
                      }`}>{s.badge > 9 ? "9+" : s.badge}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Login Greeting Brief */}
            <AnimatePresence>
              {greeting && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                  className="px-4 pt-3" data-testid="copilot-greeting">
                  <div className="p-4 rounded-xl bg-gradient-to-br from-emerald-50 via-teal-50 to-white border border-emerald-200 space-y-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-emerald-600" />
                        <div>
                          <p className="text-xs font-bold text-emerald-800">Welcome back, {greeting.userName}.</p>
                          <p className="text-[10px] text-emerald-600">
                            {signals.length > 0 ? `${signals.length} signal${signals.length > 1 ? "s" : ""} detected.` : ""}
                            {greeting.highCount > 0 ? ` ${greeting.highCount} urgent.` :
                             greeting.totalCount > 0 ? ` ${greeting.totalCount} items on workflow.` : " All clear."}
                          </p>
                        </div>
                      </div>
                      <button onClick={() => setGreeting(null)} className="p-0.5 rounded hover:bg-emerald-100 shrink-0" data-testid="greeting-dismiss">
                        <X className="h-3 w-3 text-emerald-600" />
                      </button>
                    </div>
                    {greeting.topTasks?.length > 0 && (
                      <div className="space-y-1.5">
                        <p className="text-[9px] font-bold text-[#888] uppercase tracking-wide">Top Priorities</p>
                        {greeting.topTasks.map((t, i) => {
                          const ps = PRIORITY_STYLES[t.priority] || PRIORITY_STYLES.low;
                          const Icon = TASK_ICONS[t.type] || CircleDot;
                          return (
                            <div key={t.id || i} className="flex items-center gap-2 text-[11px]" data-testid={`greeting-task-${i}`}>
                              <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${ps.dot}`} />
                              <Icon className={`h-3 w-3 shrink-0 ${ps.text}`} />
                              <span className="text-[#333] truncate flex-1">{t.title}</span>
                              {t.action_label && (
                                <button onClick={() => { handleAction(t); setGreeting(null); }}
                                  className={`px-2 py-0.5 rounded-md text-[9px] font-bold whitespace-nowrap ${ps.text} border ${ps.bg} hover:shadow-sm`}>
                                  {t.action_label}
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {greeting.topBlocker && (
                      <div className="p-2 rounded-lg bg-red-50/70 border border-red-200" data-testid="greeting-blocker">
                        <div className="flex items-center gap-1.5">
                          <AlertTriangle className="h-3 w-3 text-red-500 shrink-0" />
                          <span className="text-[10px] font-bold text-red-700">Blocker:</span>
                          <span className="text-[10px] text-red-600 truncate">{greeting.topBlocker.title}</span>
                        </div>
                      </div>
                    )}
                    {greeting.nextAction && (
                      <div className="flex items-center gap-2" data-testid="greeting-next-action">
                        <Zap className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                        <span className="text-[10px] text-emerald-700 flex-1 truncate">
                          <span className="font-bold">Next:</span> {greeting.nextAction.title}
                        </span>
                        {greeting.nextAction.action_label && (
                          <button onClick={() => { handleAction(greeting.nextAction); setGreeting(null); }}
                            data-testid="greeting-next-action-btn"
                            className="px-2.5 py-1 rounded-lg bg-emerald-700 text-white text-[9px] font-bold hover:bg-emerald-600 whitespace-nowrap">
                            {greeting.nextAction.action_label}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />
                </div>
              ) : (
                <>
                  {activeSection === "tasks" && (
                    <div className="space-y-2" data-testid="copilot-tasks-section">
                      {mergedAttention.length === 0 ? (
                        <div className="text-center py-8 text-sm text-[#bbb]">
                          <Check className="h-8 w-8 mx-auto mb-2 opacity-30" />
                          <p>All clear! No items need attention.</p>
                        </div>
                      ) : (
                        mergedAttention.map((item, i) =>
                          item._isSignal ? (
                            <SignalItem key={`sig-${item.signal_id}`} signal={item}
                              onNavigate={handleEntityNavigate} onConfirmAction={(sig) => setConfirmSignal(sig)} />
                          ) : (
                            <TaskItem key={item.id || `task-${i}`} task={item} onAction={handleAction} />
                          )
                        )
                      )}
                    </div>
                  )}

                  {activeSection === "next" && (
                    <div data-testid="copilot-next-section">
                      <NextActionCard
                        action={context?.next_action}
                        signal={nba?.signal ? { ...nba.signal, description: nba.reason, recommended_action: nba.action } : null}
                        onAction={(item) => item.signal_id ? setConfirmSignal(item) : handleAction(item)}
                        onNavigate={handleEntityNavigate} />
                      {!context?.next_action && !nba && (
                        <div className="text-center py-8 text-sm text-[#bbb]">
                          <Sparkles className="h-8 w-8 mx-auto mb-2 opacity-30" />
                          <p>No recommended action right now.</p>
                        </div>
                      )}
                    </div>
                  )}

                  {activeSection === "blockers" && (
                    <div className="space-y-2" data-testid="copilot-blockers-section">
                      {v2Blockers.length === 0 ? (
                        <div className="text-center py-8 text-sm text-[#bbb]">
                          <Check className="h-8 w-8 mx-auto mb-2 opacity-30" />
                          <p>No blockers detected.</p>
                        </div>
                      ) : (
                        v2Blockers.map((b, i) => (
                          <BlockerItem key={b.signal_id || `blocker-${i}`} blocker={b} onNavigate={handleEntityNavigate} />
                        ))
                      )}
                    </div>
                  )}

                  {activeSection === "chat" && (
                    <ChatSection leadId={focusedLeadId} />
                  )}

                  {activeSection === "memory" && (
                    <MemorySection leadId={focusedLeadId} />
                  )}

                  {activeSection === "summary" && (
                    <QuickSummary summary={context?.summary} role={context?.role}
                      aiSummary={v2Summary?.ai_summary} signalCounts={signalCounts} />
                  )}
                </>
              )}
            </div>

            {/* Focused Deal Banner */}
            {context?.focused_deal && (
              <div className="px-4 py-2.5 border-t border-[#e8e0d0] bg-[#faf7f2] shrink-0">
                <div className="flex items-center gap-2 text-xs">
                  <FileText className="h-3 w-3 text-emerald-600 shrink-0" />
                  <span className="font-semibold text-[#333]">{context.focused_deal.deal_number}</span>
                  <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                    {context.focused_deal.stage_label}
                  </span>
                  <span className="text-[10px] text-[#999] ml-auto">Owner: {context.focused_deal.owner}</span>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
